import SimpleModal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { CheckInEmailReportType } from "app/containers/WeeklyCheckInMonitor/types";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  resetSendSampleWeeklyCheckInEmail,
  selectSendSampleWeeklyCheckInEmailStatus,
} from "app/containers/WeeklyCheckInMonitor/slice";
import { useEffect, useRef, useState } from "react";
import { getUserNotificationsAndEmailSettings } from "app/containers/UserSettings/slice";
import {
  resetChatbotState,
  selectSampleChatState,
  selectThreadId,
  setSampleChatState,
  setThreadId,
} from "app/containers/Chatbot/slice";
import CheckInEmailReport from "./CheckInEmailReport";
import EmployeeConversationSummaryReport from "./EmployeeConversationSummaryReport";
import TeamLeaderReport from "./TeamLeaderReport";
import SeniorManagerReport from "./SeniorManagerReport";
import ExecutiveReport from "./ExecutiveReport";
import SampleWeeklyCheckInChat from "./SampleWeeklyCheckInChat";
import SampleWeeklyCheckInPreviewModalHeaderAlt from "./SampleWeeklyCheckInPreviewModalHeader";
import {
  faComments,
  faLayerGroup,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Nav } from "react-bootstrap";
import { onSampleWeeklyCheckInClick } from "app/components/QuickActions/helpers";
import { selectCurrentUserInfo } from "app/containers/Global/slice";

interface Props {
  type?: CheckInEmailReportType | "sample-weekly-check-in-chat";
  isOpen?: boolean;
  onModalClose?: () => void;
  backdrop?: boolean;
  onTypeChange: (
    type: CheckInEmailReportType | "sample-weekly-check-in-chat"
  ) => void;
  closeButtonText?: string;
}

const SampleWeeklyCheckInEmailReportsModal = ({
  type,
  isOpen,
  onModalClose,
  backdrop,
  onTypeChange,
  closeButtonText = "Close",
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const contentBodyRef = useRef<HTMLDivElement>(null);
  const sendSampleWeeklyCheckInEmailStatus = useAppSelector(
    selectSendSampleWeeklyCheckInEmailStatus
  );
  const threadId = useAppSelector(selectThreadId);
  const usersInfo = useAppSelector(selectCurrentUserInfo);
  const sampleChatState = useAppSelector(selectSampleChatState);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState<boolean>(true);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    dispatch(getUserNotificationsAndEmailSettings());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (sendSampleWeeklyCheckInEmailStatus !== "succeeded" || !type) {
      return;
    }
    dispatch(resetSendSampleWeeklyCheckInEmail());
  }, [dispatch, sendSampleWeeklyCheckInEmailStatus, type]);

  const onClose = () => {
    onModalClose?.();
    dispatch(resetSendSampleWeeklyCheckInEmail());
    if (threadId) {
      dispatch(setThreadId(null));
      dispatch(resetChatbotState());
      dispatch(setSampleChatState({ isSampleChatShowing: false }));
    }
  };

  const getModalBody = () => {
    switch (type) {
      case "check-in-email":
        return (
          <CheckInEmailReport
            onChatClick={() => {
              onTypeChange("sample-weekly-check-in-chat");
              sampleWeeklyCheckInClick();
            }}
          />
        );
      case "employee-conversation-summary-report":
        return <EmployeeConversationSummaryReport />;
      case "team-leader-report":
        return <TeamLeaderReport />;
      case "senior-manager-report":
        return <SeniorManagerReport />;
      case "executive-report":
        return <ExecutiveReport />;
      case "sample-weekly-check-in-chat":
        return <SampleWeeklyCheckInChat onClose={onClose} />;
    }
  };

  const navConstant: {
    title: string;
    items: { label: string; value: string }[];
    fontAwesomeIcon?: IconDefinition;
  }[] = [
    {
      title: "Core Check-Ins",
      items: [
        {
          label: "Invite Email",
          value: "check-in-email",
        },
        {
          label: "Check-In Chat",
          value: "sample-weekly-check-in-chat",
        },
        {
          label: "Check-In Summaries",
          value: "employee-conversation-summary-report",
        },
      ],
      fontAwesomeIcon: faComments,
    },
    {
      title: "Insight Reports",
      items: [
        {
          label: "Manager / Team Leader",
          value: "team-leader-report",
        },
        {
          label: "Senior Manager",
          value: "senior-manager-report",
        },
        {
          label: "Executive Intelligence",
          value: "executive-report",
        },
      ],
      fontAwesomeIcon: faLayerGroup,
    },
  ];

  const sampleWeeklyCheckInClick = () => {
    if (sampleChatState.isSampleChatShowing) {
      return;
    }

    onSampleWeeklyCheckInClick(usersInfo?.firstName ?? "", dispatch);
  };

  const onNavClick = (updatedType: string) => {
    if (contentBodyRef.current && updatedType !== type) {
      contentBodyRef.current.scrollTop = 0;
    }

    // If the updatedType is changed to sample-weekly-check-in-chat and the sample chat is not showing,
    // then we need to call onSampleWeeklyCheckInClick to open the sample chat and set initial messages
    if (updatedType === "sample-weekly-check-in-chat") {
      sampleWeeklyCheckInClick();
    }

    switch (updatedType) {
      case "check-in-email":
      case "sample-weekly-check-in-chat":
      case "employee-conversation-summary-report":
      case "team-leader-report":
      case "senior-manager-report":
      case "executive-report":
        onTypeChange?.(updatedType);
        break;
      default:
        return;
    }
  };

  const getDesktopNavItems = (navItem: { label: string; value: string }) => (
    <button
      key={navItem.value}
      className={`sample-weekly-check-nav-desktop__item ${
        type === navItem.value ? "active" : ""
      }`}
      onClick={() => {
        onNavClick(navItem.value);
      }}
    >
      <p>{navItem.label}</p>
    </button>
  );

  const getNavigation = () => (
    <div className="sample-weekly-check-nav-desktop">
      {navConstant.map((navContainer) => (
        <div
          key={navContainer.title}
          className="sample-weekly-check-nav-desktop__container"
        >
          <div className="sample-weekly-check-nav-desktop__container-title">
            {navContainer.fontAwesomeIcon ? (
              <div className="small-square-icon">
                <FontAwesomeIcon
                  icon={navContainer.fontAwesomeIcon}
                  className="icon"
                />
              </div>
            ) : null}
            <span className="sample-weekly-check-nav-desktop__title">
              {navContainer.title}
            </span>
          </div>
          <div className="sample-weekly-check-nav-desktop__container-items">
            {navContainer.items.map(getDesktopNavItems)}
          </div>
        </div>
      ))}
    </div>
  );

  const getMobileNavigation = () => {
    const sectionShowing = navConstant.find((navContainer) => {
      return navContainer.items.find((navItem) => {
        return navItem.value === type;
      });
    });

    const navItems = navConstant.map((navContainer) => (
      <Nav.Item key={navContainer.title}>
        <Nav.Link eventKey={navContainer.title}>{navContainer.title}</Nav.Link>
      </Nav.Item>
    ));
    return (
      <div className="sample-weekly-check-nav-mobile">
        <Nav
          className="simple-nav sample-weekly-check-nav-mobile__section-nav"
          activeKey={sectionShowing?.title}
          onSelect={(e) => {
            if (!e) return;
            const updatedSectionShowing = navConstant.find((navContainer) => {
              return navContainer.title === e;
            });
            if (!updatedSectionShowing) return;
            onNavClick(updatedSectionShowing.items[0].value);
          }}
        >
          {navItems}
        </Nav>
        <div className="sample-weekly-check-nav-mobile__item-container">
          {sectionShowing?.items.map((navItem) => {
            return (
              <button
                key={navItem.value}
                onClick={() => {
                  onNavClick(navItem.value);
                }}
                className={`sample-weekly-check-nav-mobile__item ${
                  type === navItem.value ? "active" : ""
                }`}
              >
                {navItem.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SimpleModal
      show={isOpen}
      onHide={onClose}
      title="Preview Check-Ins Experience"
      size="xl"
      className="sample-weekly-check-in-modal"
      backdrop={backdrop}
    >
      <div className="sample-weekly-check-in-modal-body">
        {getNavigation()}
        {getMobileNavigation()}
        <div className="sample-weekly-check-in-modal-body__content">
          <SampleWeeklyCheckInPreviewModalHeaderAlt
            type={type ?? ""}
            isCollapsed={isHeaderCollapsed}
            onCollapseClick={(isCollapsed) => setIsHeaderCollapsed(isCollapsed)}
          />
          <div
            className="sample-weekly-check-in-modal-body__content__body"
            ref={contentBodyRef}
          >
            {getModalBody()}
          </div>
        </div>
      </div>
      <div className="sample-weekly-check-in-modal-footer">
        <Button
          onClick={onClose}
          variant="secondary-blue"
          className="border-0 ms-auto"
        >
          {closeButtonText}
        </Button>
      </div>
    </SimpleModal>
  );
};

export default SampleWeeklyCheckInEmailReportsModal;
